import { Injectable } from '@angular/core';
import mixpanel from 'mixpanel-browser';
import { Observable, Subject } from 'rxjs';
import {
  AnalyticsConfig,
  AnalyticsUser,
  TrackedEvent,
} from './models/analytics.models';
import { AnalyticsEventDefinition } from './models/event.models';

/**
 * Central analytics facade. Every custom event in the app should be logged
 * through this service rather than by calling an analytics SDK directly.
 *
 * This is the ONLY place that talks to Mixpanel. To switch to a different
 * analytics tool, reimplement the method bodies here — no callers across the
 * app need to change.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private enabled = false;

  private readonly trackedSubject = new Subject<TrackedEvent>();
  /**
   * Emits each event as it is tracked. Intended for debug tooling (e.g. a
   * toast that confirms what was logged); does not affect what is sent.
   */
  readonly tracked$: Observable<TrackedEvent> =
    this.trackedSubject.asObservable();

  /** Initialise the analytics backend. Call once, on app start. */
  init(config: AnalyticsConfig): void {
    this.enabled = Boolean(config.enabled && config.token);
    if (!this.enabled) {
      return;
    }

    mixpanel.init(config.token as string, {
      autocapture: false,
      record_sessions_percent: 100,
    });

    if (config.environment) {
      // Super property — attached to every event for dashboard filtering.
      mixpanel.register({ environment: config.environment });
    }
  }

  /** Associate subsequent events with a signed-in user. */
  identifyUser(user: AnalyticsUser): void {
    if (!this.enabled) {
      return;
    }

    mixpanel.identify(user.id);
    mixpanel.people.set({
      $name: user.name,
      $email: user.email,
      ...(user.prsId ? { prsId: user.prsId } : {}),
      ...(user.createdAt
        ? { $created: new Date(user.createdAt).toISOString() }
        : {}),
      ...(user.accountId
        ? { accountId: user.accountId, accountName: user.accountName }
        : {}),
    });
  }

  /**
   * Track an analytics event. Pass the event definition object from the
   * per-tab events file (e.g. `DASHBOARD_EVENTS.DASHBOARD_OUTSTANDING_TASK_SELECTED`).
   * Optionally pass `properties` for event-specific context (ids, values, etc.).
   *
   * The event name is composed as "Tab | Section | Action" and the tiers are
   * also sent as `tab` / `section` / `action` properties so reports can be
   * broken down or filtered by any tier.
   */
  track(
    def: AnalyticsEventDefinition,
    properties?: Record<string, unknown>
  ): void {
    if (!this.enabled) {
      return;
    }

    const name = [def.tab, def.section, def.action].filter(Boolean).join(' | ');

    const props: Record<string, unknown> = {
      tab: def.tab,
      ...(def.section ? { section: def.section } : {}),
      action: def.action,
      ...properties,
    };

    mixpanel.track(name, props);
    this.trackedSubject.next({ name, properties: props });
  }

  /** Clear the current identity. Call on logout. */
  reset(): void {
    if (!this.enabled) {
      return;
    }
    mixpanel.reset();
  }
}
